import { MAP_FURNITURE_KINDS, type MapFurnitureKind } from "@/lib/office/personal-office-layout";
import { FLOOR_TILES, FURNITURE_SPRITES, WALL_TILES } from "@/lib/office/sprites/catalog";
import type {
  DoorWall,
  Footprint,
  FurniturePlacementDefaults,
  ResolvedFurnitureSprite,
  SpriteVariant,
  TileSpriteDef,
} from "@/lib/office/sprites/types";

const DOOR_WALLS: readonly DoorWall[] = ["N", "S", "E", "W"];

function isDoorWall(v: string): v is DoorWall {
  return (DOOR_WALLS as readonly string[]).includes(v);
}

function isMapFurnitureKind(v: string): v is MapFurnitureKind {
  return (MAP_FURNITURE_KINDS as readonly string[]).includes(v);
}

function pickVariant(
  variants: readonly SpriteVariant[],
  dir: DoorWall,
  style?: string,
): { asset: SpriteVariant | null; reason?: ResolvedFurnitureSprite["reason"] } {
  if (variants.length === 0) return { asset: null, reason: "no_asset" };

  const styleKey = style?.trim().toLowerCase() || "";
  const styled = styleKey
    ? variants.filter((v) => v.styles?.some((s) => s.toLowerCase() === styleKey))
    : variants.filter((v) => !v.styles || v.styles.length === 0);
  const pool = styled.length > 0 ? styled : styleKey ? [] : variants;

  // Style-specific kinds (buildings) with unknown style → no asset (procedural fallback).
  if (styleKey && pool.length === 0) {
    const anyStyled = variants.some((v) => v.styles && v.styles.length > 0);
    if (anyStyled) return { asset: null, reason: "no_variant_for_dir" };
  }

  const candidates = pool.length > 0 ? pool : variants;
  const exact = candidates.find((v) => !v.dirs || v.dirs.includes(dir));
  if (exact) return { asset: exact };

  const south = candidates.find((v) => !v.dirs || v.dirs.includes("S"));
  if (south) return { asset: south, reason: "no_variant_for_dir" };

  const first = candidates[0];
  if (!first) return { asset: null, reason: "no_asset" };
  return { asset: first, reason: "no_variant_for_dir" };
}

/**
 * Resolve which furniture sprite asset to draw for a kind + facing (+ optional style).
 * Does not check image load state — that is the loader's job.
 */
export function resolveFurnitureSprite(
  kind: string,
  dirIn: string = "S",
  styleIn?: string,
): ResolvedFurnitureSprite {
  const dir: DoorWall = isDoorWall(dirIn) ? dirIn : "S";
  const style = styleIn?.trim() || undefined;

  if (!isMapFurnitureKind(kind)) {
    return {
      kind,
      dir,
      footprint: { w: 1, d: 1 },
      depthBias: 0,
      defaultSeat: false,
      defaultBlock: false,
      useSprite: false,
      asset: null,
      thumbSrc: null,
      reason: "unknown_kind",
    };
  }

  const entry = FURNITURE_SPRITES[kind];
  const picked = pickVariant(entry.variants, dir, style);
  const useSprite = entry.useSprite !== false;

  return {
    kind,
    dir,
    footprint: entry.footprint,
    depthBias: entry.depthBias ?? 0,
    defaultSeat: entry.defaultSeat === true,
    defaultBlock: entry.defaultBlock === true,
    useSprite,
    asset: useSprite ? picked.asset : null,
    thumbSrc: entry.thumbSrc ?? picked.asset?.src ?? null,
    reason: useSprite ? picked.reason : "procedural_only",
  };
}

/** Catalog defaults for admin placeFurniture (layout w/d still stored on the row). */
export function furniturePlacementDefaults(kind: string): FurniturePlacementDefaults {
  if (!isMapFurnitureKind(kind)) {
    return { w: 1, d: 1 };
  }
  const entry = FURNITURE_SPRITES[kind];
  const out: FurniturePlacementDefaults = {
    w: entry.footprint.w,
    d: entry.footprint.d,
  };
  if (entry.defaultSeat) out.seat = true;
  if (entry.defaultBlock) out.block = true;
  return out;
}

export function resolveFloorTile(id: string): TileSpriteDef | null {
  return FLOOR_TILES[id] ?? null;
}

export function resolveWallTile(id: string): TileSpriteDef | null {
  return WALL_TILES[id] ?? null;
}

export function furnitureFootprint(kind: string): Footprint {
  return furniturePlacementDefaults(kind);
}

/**
 * Unique sprite srcs needed for a layout's free furniture list.
 * Callers also warm desk/chair/plant for auto-furnished pods.
 */
export function collectLayoutSpriteSrcs(
  furniture: ReadonlyArray<{ kind: string; dir?: string; style?: string }>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of furniture) {
    const r = resolveFurnitureSprite(f.kind, f.dir ?? "S", f.style);
    if (r.asset && !seen.has(r.asset.src)) {
      seen.add(r.asset.src);
      out.push(r.asset.src);
    }
  }
  return out;
}

/** Core kinds always present in HQ (pods + common auto furniture). */
export function collectEssentialSpriteSrcs(): string[] {
  const kinds: Array<{ kind: string; dir: DoorWall }> = [
    { kind: "desk", dir: "N" },
    { kind: "desk", dir: "S" },
    { kind: "desk", dir: "E" },
    { kind: "desk", dir: "W" },
    { kind: "chair", dir: "N" },
    { kind: "chair", dir: "S" },
    { kind: "chair", dir: "E" },
    { kind: "chair", dir: "W" },
    { kind: "plant", dir: "S" },
    { kind: "table", dir: "S" },
    { kind: "conftable", dir: "S" },
    { kind: "fox", dir: "S" },
  ];
  return collectLayoutSpriteSrcs(kinds);
}
